import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

// Eager: layouts + auth routes. These are tiny and on the critical first-
// paint path, so lazy-loading wouldn't help and would add a flash of nothing.
import { AuthLayout } from "@/routes/auth/AuthLayout";
import { SignIn } from "@/routes/auth/SignIn";
import { AcceptInvite } from "@/routes/auth/AcceptInvite";
import { ForgotPassword } from "@/routes/auth/ForgotPassword";
import { SignupRedirect } from "@/routes/auth/SignupRedirect";
import { NoOrgAccess } from "@/routes/auth/NoOrgAccess";
import { RedirectIfAuthed, RequireOrgAccess } from "@/lib/session";
import { AppLayout } from "@/routes/app/AppLayout";
import { Home } from "@/routes/app/Home";
import { NotFound } from "@/routes/NotFound";

// Lazy: the heavy authenticated routes. Each pulls a portion of the recharts
// / pane bundles. Splitting cuts the auth+home bundle dramatically and means
// "first paint" doesn't wait for Analytics + ProgramEditor + CohortDetail.
const ProgramList = lazy(() =>
  import("@/routes/app/programs/ProgramList").then((m) => ({
    default: m.ProgramList,
  })),
);
const ProgramEditor = lazy(() =>
  import("@/routes/app/programs/ProgramEditor").then((m) => ({
    default: m.ProgramEditor,
  })),
);
const CohortList = lazy(() =>
  import("@/routes/app/cohorts/CohortList").then((m) => ({
    default: m.CohortList,
  })),
);
const CohortDetail = lazy(() =>
  import("@/routes/app/cohorts/CohortDetail").then((m) => ({
    default: m.CohortDetail,
  })),
);
const Analytics = lazy(() =>
  import("@/routes/app/Analytics").then((m) => ({ default: m.Analytics })),
);
const Codes = lazy(() =>
  import("@/routes/app/Codes").then((m) => ({ default: m.Codes })),
);
const Outreach = lazy(() =>
  import("@/routes/app/Outreach").then((m) => ({ default: m.Outreach })),
);
const Sandbox = lazy(() =>
  import("@/routes/app/Sandbox").then((m) => ({ default: m.Sandbox })),
);
const Team = lazy(() =>
  import("@/routes/app/Team").then((m) => ({ default: m.Team })),
);
const SettingsLayout = lazy(() =>
  import("@/routes/app/Settings").then((m) => ({
    default: m.SettingsLayout,
  })),
);
const OrgSettings = lazy(() =>
  import("@/routes/app/Settings").then((m) => ({
    default: m.OrgSettings,
  })),
);
const ProfileSettings = lazy(() =>
  import("@/routes/app/Settings").then((m) => ({
    default: m.ProfileSettings,
  })),
);

/** Wraps a lazy route in a Suspense boundary. The per-route ErrorBoundary
 * lives in AppLayout, so any throw in the lazy import surfaces there too. */
function Lazy({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="anim-fade-up text-[14px] text-[var(--color-slate-300)] py-10 text-center">
          Loading…
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AuthLayout />,
    children: [
      {
        path: "signin",
        element: (
          <RedirectIfAuthed>
            <SignIn />
          </RedirectIfAuthed>
        ),
      },
      { path: "accept-invite/:token", element: <AcceptInvite /> },
      { path: "forgot", element: <ForgotPassword /> },
      { path: "signup", element: <SignupRedirect /> },
      { path: "no-access", element: <NoOrgAccess /> },
    ],
  },
  {
    path: "/",
    element: (
      <RequireOrgAccess>
        <AppLayout />
      </RequireOrgAccess>
    ),
    children: [
      { index: true, element: <Home /> },
      {
        path: "programs",
        element: (
          <Lazy>
            <ProgramList />
          </Lazy>
        ),
      },
      {
        path: "programs/:id",
        element: (
          <Lazy>
            <ProgramEditor />
          </Lazy>
        ),
      },
      {
        path: "cohorts",
        element: (
          <Lazy>
            <CohortList />
          </Lazy>
        ),
      },
      {
        path: "cohorts/:id",
        element: (
          <Lazy>
            <CohortDetail />
          </Lazy>
        ),
      },
      {
        path: "analytics",
        element: (
          <Lazy>
            <Analytics />
          </Lazy>
        ),
      },
      {
        path: "codes",
        element: (
          <Lazy>
            <Codes />
          </Lazy>
        ),
      },
      {
        path: "outreach",
        element: (
          <Lazy>
            <Outreach />
          </Lazy>
        ),
      },
      {
        path: "sandbox",
        element: (
          <Lazy>
            <Sandbox />
          </Lazy>
        ),
      },
      {
        path: "team",
        element: (
          <Lazy>
            <Team />
          </Lazy>
        ),
      },
      {
        path: "settings",
        element: (
          <Lazy>
            <SettingsLayout />
          </Lazy>
        ),
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <OrgSettings />
              </Lazy>
            ),
          },
          {
            path: "profile",
            element: (
              <Lazy>
                <ProfileSettings />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
  { path: "/404", element: <NotFound /> },
  { path: "*", element: <Navigate to="/404" replace /> },
]);
